import {
  finishReceivingMessages,
  interruptTextToSpeech,
  setResult,
  startReceivingMessages,
  stopTextToSpeech,
  updateAttachmentProgress,
  updateTextToSpeechLoading,
  updateTextToSpeechStatus,
  updateVoiceModChatStatus,
} from '@webclient/state/slices/search-session';
import disconnectFromRoom from '@webclient/state/slices/search-session/thunks/disconnectAsyncRoom';
import transformMessage from '@webclient/state/slices/search-session/thunks/utils/transformMessage';
import { roomInstance } from '@webclient/state/slices/search-session/utils/room-manager';
import { RootType } from '@webclient/state/store';

import { LiveKitRealTimeResult } from '@webclient/shared-types/search-result/streaming';

import { createAsyncThunk } from '@reduxjs/toolkit';
import {
  DataPacket_Kind,
  LivekitError,
  RemoteParticipant,
  Room,
  RoomEvent,
} from 'livekit-client';

const isItDev = import.meta.env.DEV;

const connectAsyncToRoom = createAsyncThunk(
  'searchSession/connectToRoom',
  async (
    { audio = false }: { audio?: boolean },
    { rejectWithValue, dispatch, getState },
  ) => {
    function handleDataReceived(
      payload: Uint8Array,
      _participant?: RemoteParticipant,
      _kind?: DataPacket_Kind,
      _topic?: string,
    ) {
      const decoder = new TextDecoder();
      const decodedPayload = decoder.decode(payload);
      const stateResult = (getState() as RootType).searchSession.result;
      const parsedPayload = JSON.parse(decodedPayload) as LiveKitRealTimeResult;
      const transformedMessage = transformMessage(parsedPayload, stateResult);

      dispatch(setResult(transformedMessage));

      if (isItDev && parsedPayload.type === 'files_status') {
        // eslint-disable-next-line no-restricted-syntax
        console.log('files_status', parsedPayload);
      }

      if (parsedPayload.type === 'file_status_added') {
        dispatch(
          updateAttachmentProgress({
            progress: 70, //? 50% for uploading, 50% for processing by ai
            error: null,
            name: parsedPayload.name,
            id: parsedPayload.id,
          }),
        );
      }

      if (parsedPayload.type === 'file_status_processed') {
        dispatch(
          updateAttachmentProgress({
            id: parsedPayload.id,
            name: parsedPayload.name,
            progress: 100, //? 50% for uploading, 50% for processing by ai
            error: null,
          }),
        );
      }

      if (parsedPayload.type === 'file_status_error') {
        dispatch(
          updateAttachmentProgress({
            id: parsedPayload.id,
            error: parsedPayload.response,
            name: parsedPayload.name,
            progress: 0,
          }),
        );
      }

      if (parsedPayload.type === 'realtime_status') {
        if ('state' in parsedPayload) {
          dispatch(updateVoiceModChatStatus(parsedPayload.state));
          return;
        }
      }

      if (parsedPayload.type === 'status') {
        if (parsedPayload.response === 'starting layout') {
          dispatch(startReceivingMessages());
          return;
        }

        if (parsedPayload.response === 'finished') {
          dispatch(finishReceivingMessages());
          return;
        }
      }

      if ('update' in parsedPayload) {
        if (parsedPayload.update === 'playback queued') {
          dispatch(updateTextToSpeechStatus('stop'));
        }
        if (parsedPayload.update === 'playback starting') {
          dispatch(updateTextToSpeechLoading(false));
        }
        if (parsedPayload.update === 'playback interrupted') {
          dispatch(interruptTextToSpeech());
        }
        if (parsedPayload.update === 'playback completed') {
          dispatch(stopTextToSpeech());
        }
      }
    }

    try {
      const room = new Room();
      roomInstance.set(room);
      const token = (getState() as RootType).searchSession.token;
      await room.connect(import.meta.env.VITE_LIVE_KIT_SERVER_URL, token!);
      if (audio) {
        await room.localParticipant.setMicrophoneEnabled(audio);
      }
      room.on(RoomEvent.DataReceived, handleDataReceived);
      room.on(RoomEvent.Disconnected, async () => {
        await dispatch(disconnectFromRoom());
      });
    } catch (error) {
      rejectWithValue((error as LivekitError).message);
    }
  },
);

export default connectAsyncToRoom;
